import { BrowserSupport } from '@tableau/browser-support';
import * as _ from 'underscore';
import { CheckboxState } from '../../common/components/checkboxes/CheckboxState';
import { Filter } from '../helpers/Filter';
import { Order } from '../helpers/Order';
import { L10n } from '../L10n/L10n';
import { IColumnWidths } from '../models/ColumnWidths';
import { IApplyCancelButtons } from '../models/IApplyCancelButtons';
import { ISliceable } from '../models/ISliceable';
import { OrderKeys } from '../models/OrderKeys';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Groups } from '../../userManagement/groups/Groups';
import { SiteUsers } from '../../userManagement/services/resources/SiteUsers';
import {
  GetUsersGroupMembershipRequest,
  IGetUsersGroupMembershipResult,
} from '../ServerApi/requests/GetUsersGroupMembershipRequest';
import {
  IUpdateUsersGroupMembershipParams,
  IUpdateUsersGroupMembershipResult,
} from '../ServerApi/requests/UpdateUsersGroupMembershipRequest';
import { IGroup } from '../ServerApi/types/IGroup';
import { IGroupMember } from '../ServerApi/types/IGroupMember';
import { IOrderClause } from '../ServerApi/types/IOrderClause';
import { IUser } from '../ServerApi/types/IUser';
import { ServerService } from '../services/ServerService';
import { SliceableFactory } from '../services/SliceableFactory';
import { GroupMembershipUpdateActionNotification } from '../../userManagement/groups/services/notifications/GroupMembershipUpdateActionNotification';
import { GrantLicenseModes } from '../ServerApi/requests/SyncActiveDirectoryGroupRequest';

export let GroupMembershipEvent = {
  MembershipChanged: 'MembershipChanged'
};

export let GroupMembershipDialogFilters = {
  AllGroups: 'groupMembershipAllGroups',
  AssignedGroups: 'groupMembershipAssignedGroups',
  SearchResults: 'groupMembershipSearchResults'
};

export interface IGroupMembershipDialogScope extends ng.IScope {
  applyCancelButtons: IApplyCancelButtons;
  busy: boolean;
  checkboxClicked: (group: IGroup, colIndex?: number) => void;
  clientState: _.Dictionary<string>;
  close: (result: any) => void; // from modal scope
  dismiss: (reason?: any) => void; // from modal scope
  editGroupMembershipDescription: string;
  isGroupDirty: (group: IGroup) => boolean;
  isDirty: () => boolean;
  sliceable: ISliceable<IGroup>;
  users: Set<IUser>;
  submitSearch: (searchText: string, $event?: JQueryEventObject) => void;
  groupFilters: string[];
  groupFilter: GroupMembershipDialogCtrl.IFilterSelection;
  lastGroupFilter: string;
  searchInput: GroupMembershipDialogCtrl.ISearchInput;
  refreshAssignedGroups: () => void;
  needsAssignedGroupsRefresh: boolean;
  columnWidths: IColumnWidths;
  canManageUsersForGroup(group: IGroup): boolean;
}

export class GroupMembershipDialogCtrl {

  public static $inject = [
    '$q',
    '$scope',
    '$translate',
    'ServerService',
    'SliceableFactory'
  ];

  private static ListOrder: IOrderClause[] = [ Order.asc(OrderKeys.name) ];

  private clientChanges: _.Dictionary<string>;
  private serverState: _.Dictionary<string>;
  private groupIdToGroup: { [id: string]: IGroup };
  private uncheckedAssignedGroups: number;
  constructor(
    private $q: ng.IQService,
    private scope: IGroupMembershipDialogScope,
            $translate: ng.translate.ITranslateService,
    private server: ServerService,
    private sliceableFactory: SliceableFactory) {

    this.server = server;
    this.serverState = {};
    this.groupIdToGroup = {};
    this.clientChanges = {};
    this.scope.busy = false;
    this.scope.needsAssignedGroupsRefresh = false;
    this.scope.sliceable = Groups.all().withOrder(GroupMembershipDialogCtrl.ListOrder);

    this.scope.columnWidths = {
      getActual: this.getColumnWidths,
      getMin: this.getColumnWidths,
      hasPreferredWidths: () => { return true; }
    };

    this.uncheckedAssignedGroups = 0;

    this.scope.groupFilter = { selection: GroupMembershipDialogFilters.AllGroups };
    this.scope.lastGroupFilter = this.scope.groupFilter.selection;
    this.updateGroupFilters();

    this.scope.searchInput = { text: '' };
    const displayName = (scope.users.size() > 0) ? scope.users.first().displayName : '';
    this.scope.editGroupMembershipDescription =
      $translate.instant(
        'editGroupMembershipDescription',
        { USER_COUNT: scope.users.size(), USER_NAME: L10n.elideIfLong(displayName) },
        L10n.Interpolations.Count);

    this.scope.isDirty = () => {
      return !_.isEmpty(this.clientChanges);
    };

    // IApplyCancelButtons
    this.scope.applyCancelButtons = {
      applyString:
        $translate.instant('save'),
      cancelString:
        $translate.instant('cancel'),
      apply:
        () => this.apply(),
      cancel:
        () => this.scope.dismiss('cancel'),
      disableApply:
        () => (!this.scope.isDirty() || this.scope.busy),
      disableCancel:
        () => this.scope.busy,
      isBusy:
        () => this.scope.busy,
    };

    this.scope.isGroupDirty = (group: IGroup) => {
      return !!this.clientChanges[group.id];
    };

    this.scope.checkboxClicked = (group: IGroup, colIndex?: number) => {
      if (group.isAllUsersGroup || !this.scope.canManageUsersForGroup(group)) { return; }

      if (this.scope.groupFilter.selection === GroupMembershipDialogFilters.AssignedGroups) {
        //keep track of the checkbox changes made to the assigned groups
        if (this.scope.clientState[group.id] === CheckboxState.empty) {
          this.uncheckedAssignedGroups--;
        } else if (this.scope.clientState[group.id] === CheckboxState.checked) {
          this.uncheckedAssignedGroups++;
        }
        //if the number of checked/mixed groups matches the original number, a refresh isn't needed.
        this.scope.needsAssignedGroupsRefresh = this.uncheckedAssignedGroups > 0;
      }

      this.scope.clientState[group.id] = CheckboxState.toggle(this.scope.clientState[group.id]);

      // if the client & server state are equal, remove the group from client changes
      // to avoid sending no-op requests to the server.
      if (this.isClientAndServerStateEqualFor(group.id)) {
        delete this.clientChanges[group.id];
      } else {
        this.clientChanges[group.id] = this.scope.clientState[group.id];
        this.groupIdToGroup[group.id] = this.clone(group);
      }
    };

    this.scope.submitSearch = (searchText: string, $event?: JQueryEventObject) => {
      if (!searchText) {
        this.scope.groupFilter.selection = this.scope.lastGroupFilter;
      } else {
        if (this.scope.groupFilter.selection !== GroupMembershipDialogFilters.SearchResults) {
          this.scope.groupFilter.selection = GroupMembershipDialogFilters.SearchResults;
        }
        this.scope.sliceable = Groups.all().withFiltersAndOrder([Filter.search(searchText)], GroupMembershipDialogCtrl.ListOrder);
      }

      if (BrowserSupport.IsTouch() && $event) {
        // blur to retract the keyboard when search is explicitly submitted
        $($event.target).trigger('blur');
      }
    };

    this.scope.canManageUsersForGroup = (group: IGroup): boolean => {
      if (group.grantLicenseMode === GrantLicenseModes.ON_LOGIN && !server.canSessionUserManageUsers()) {
        return false;
      }
      return true;
    };

    this.scope.$watch('searchInput.text', (text: string) => {
      this.scope.submitSearch(text);
    });

    this.scope.refreshAssignedGroups = () => {
      this.scope.sliceable = this.getAssignedGroupsList();
      this.scope.needsAssignedGroupsRefresh = false;
    };

    this.scope.$watch('groupFilter.selection', (newFilter: string) => {
      this.updateGroupFilters();
      if(newFilter === GroupMembershipDialogFilters.AssignedGroups) {
        this.scope.refreshAssignedGroups();
      }

      if(newFilter === GroupMembershipDialogFilters.AllGroups) {
        this.scope.sliceable = Groups.all().withOrder(GroupMembershipDialogCtrl.ListOrder);
      }

      if(newFilter !== GroupMembershipDialogFilters.SearchResults) {
        this.scope.lastGroupFilter = newFilter;
        this.scope.searchInput.text = '';
      }
      this.scope.needsAssignedGroupsRefresh = false;
    });

    this.getServerState(this.scope.users);
  }

  //The last column's width is dependent on whether or not the scroll bar is visible.
  private getColumnWidths(index: number): number {
    //checkbox column and group name column are constant. These should be kept in sync with the design spec for the dialog.
    const widths = [27, 278];
    const calcMemberCountWidth = $('.tb-group-membership .tb-data-grid-body-background').width() - widths[0] - widths[1];

    return index === 2 ? calcMemberCountWidth : widths[index];
  }

  private updateGroupFilters(): void {
    const filters: string[] = [
      GroupMembershipDialogFilters.AllGroups,
      GroupMembershipDialogFilters.AssignedGroups
    ];
    if(this.scope.groupFilter.selection === GroupMembershipDialogFilters.SearchResults) {
      filters.push(GroupMembershipDialogFilters.SearchResults);
    }
    this.scope.groupFilters = filters;
  }

  private getAssignedGroupsList(): ISliceable<IGroup> {
    const groupsList: IGroup[] = [];
    _.each(this.scope.clientState, (checkboxState, groupId) => {
      if(checkboxState !== CheckboxState.empty) {
        groupsList.push(this.groupIdToGroup[groupId]);
      }
    });

    this.uncheckedAssignedGroups = 0;

    return this.sliceableFactory.fromArray(groupsList.sort((groupA: IGroup, groupB: IGroup) => {
      let language = (L10n.language===undefined)?
        L10n.language: L10n.language.replace('_','-');

      return groupA.name.localeCompare(groupB.name, language);
    }));
  }

  // true if either client & server state are both checked, or client state is empty and server state is null.
  // note: server state doesn't use empty for empty groups, because of pagnination -- we don't know how many
  // groups there might be, so we only track the ones that have relevant membership, and treat the nulls as empty.
  private isClientAndServerStateEqualFor(groupId: string): boolean {
    return ((this.scope.clientState[groupId] === this.serverState[groupId])
      || (this.scope.clientState[groupId] === CheckboxState.empty && !this.serverState[groupId]));
  }

  private clone(group: IGroup): IGroup {
    return {name: group.name, id: group.id, userCount: group.userCount,
            isAllUsersGroup: group.isAllUsersGroup, domainName: group.domainName, grantLicenseMode: group.grantLicenseMode};
  }

  private getServerState(users: Set<any>) {
    const userIds = users.keys();
    if (userIds && userIds.length > 0) {
      // request group membership info from the server.
      this.server.sendRequest(new GetUsersGroupMembershipRequest({ userIds: userIds }))
        .then((result: IGetUsersGroupMembershipResult) => {
          // count how many selected users belong to each group.
          // e.g. {allUsers: 3, bigGroup: 2, ...}
          const groupMembershipCounts = _.countBy(result.members, (user: IGroupMember) => user.groupId);

          // for each group, compute the checkbox state.
          // e.g. {allUsers: 'checked', bigGroup: 'mixed', ...}
          this.serverState = {};
          const selectedUsersCount = this.scope.users.size();
          _.each(groupMembershipCounts, (memberCount, groupId) => {
            this.serverState[groupId] = this.stringForGroupState(memberCount, selectedUsersCount);
          });

          this.scope.clientState = _.clone(this.serverState);
          _.each(this.clientChanges, (clientGroupState, groupId) => {
            this.scope.clientState[groupId] = clientGroupState;
          });

          _.each(result.groups, (group: IGroup) => {
            this.groupIdToGroup[group.id] = group;
          });
        });
    }
  }

  private apply(): ng.IPromise<any> {
    const users = _.clone(this.scope.users); // clone for independence from further selection
    const userIds = users.keys();

    const params: IUpdateUsersGroupMembershipParams = {
      userIds: userIds,
      addToGroupIds: [],
      removeFromGroupIds: []
    };

    // build up input params for the request: group IDs to add to & group IDs to remove from.
    _.each(this.clientChanges, (clientGroupState, groupId) => {
      if (clientGroupState === CheckboxState.checked) { params.addToGroupIds.push(groupId); }
      if (clientGroupState === CheckboxState.empty) { params.removeFromGroupIds.push(groupId); }
    });

    if (this.isValidParamsForUpdate(params)) {
      this.scope.busy = true;
      // send the update to the server.
      return SiteUsers.instance.updateUsersGroupMembership(
        params.userIds,
        params.addToGroupIds,
        params.removeFromGroupIds
      ).then((result: IUpdateUsersGroupMembershipResult) => {

        let addedToGroupName = '';
        if(params.addToGroupIds.length > 0) {
          addedToGroupName = this.groupIdToGroup[params.addToGroupIds[0]].name;
        }

        let removedFromGroupName = '';
        if(params.removeFromGroupIds.length > 0) {
          removedFromGroupName = this.groupIdToGroup[params.removeFromGroupIds[0]].name;
        }

        // show toast
        GroupMembershipUpdateActionNotification.notify(
          result,
          users,
          params.addToGroupIds.length,
          addedToGroupName,
          params.removeFromGroupIds.length,
          removedFromGroupName);

        this.scope.$emit(GroupMembershipEvent.MembershipChanged);
        this.scope.close(result);
      });
    }

    return this.$q.when({});
  }

  // Given params for making an UpdateUsersGroupMembership request
  // returns true iff there's at least one user and at least one group to add or remove the user from
  private isValidParamsForUpdate(params: IUpdateUsersGroupMembershipParams): boolean {
    return (params.userIds.length > 0 && (params.addToGroupIds.length > 0 || params.removeFromGroupIds.length > 0));
  }

  // Given the number of members in a group, and the number of selected users,
  // returns a string corresponding to the checkbox state.
  private stringForGroupState(memberCount: number, selectedUsersCount: number) {
    if (memberCount === selectedUsersCount) {
      return CheckboxState.checked;
    } else if (memberCount > 0) {
      return CheckboxState.mixed;
    } else {
      return CheckboxState.empty;
    }
  }
}

export module GroupMembershipDialogCtrl {

  export interface IFilterSelection {
    selection: string;
  }

  export interface ISearchInput {
    text: string;
  }

}

Module.instance().controller('GroupMembershipDialogCtrl', GroupMembershipDialogCtrl);
